/**
 * payment-offer-adapter — PaymentOfferResponse(snake DTO) → OfferRow(camel) 塑形（ADM-PAY-S005）
 *
 * 逐字段 snake→camel；offer 无金额 / 无多语言，scenario·eligibility 闭集串原样透传，时间 ISO 串原样
 * （展示时 fmtDateTime 本地化）。offer DTO 字段后端必填 → 无缺字段兜底需要、不编造数据。
 * scenario / eligibility 中文 label + ScenarioPicker items 一并导出，供 Editor + 列表页复用（单一来源）。
 */

import {
  OFFER_ELIGIBILITIES,
  type OfferEligibility,
  type OfferReference,
  type PaymentOfferResponse,
  type PaymentOfferScenario,
  type PaymentScenario,
} from '@novel-admin/shared';

export interface OfferRow {
  id: string;
  appId: string;
  scenario: PaymentScenario;
  startsAt: string;
  endsAt: string;
  eligibility: OfferEligibility;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** admin 只读派生：该 offer_id 仍被哪些 active banner / 未投递 push 携带（列表端点附；缺省空数组）。展示「被引用」计数 chip。*/
  references: OfferReference[];
}

export function adaptOffer(dto: PaymentOfferResponse): OfferRow {
  return {
    id: dto.id,
    appId: dto.app_id,
    scenario: dto.scenario,
    startsAt: dto.starts_at,
    endsAt: dto.ends_at,
    eligibility: dto.eligibility,
    isActive: dto.is_active,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at || dto.created_at,
    // 列表端点派生 references；单条 / 写端点不附 → 缺省空数组（不编造，无引用即空）
    references: dto.references ?? [],
  };
}

/** scenario 闭集中文 label（= app_payment_methods.scenario 闭集，复用既有支付页通道）*/
export const SCENARIO_LABELS: Record<PaymentScenario, string> = {
  charge_center: '充值中心',
  // 内建页 scenario 不进 offer 活动子集，这里仍需 label —— 本字典按 PaymentScenario 全集编译期穷尽。
  reading_pass: '小时阅读卡',
  novel_purchase: '书籍买断',
  limited_offer: '限时优惠',
  new_user_offer: '新人优惠',
  operational_popup: '运营弹窗',
};

/** eligibility 闭集中文 label */
export const ELIGIBILITY_LABELS: Record<OfferEligibility, string> = {
  all: '全部用户',
  new_user: '新用户',
};

/**
 * ScenarioPicker 卡片 items（= 原型 data.jsx OFFER_SCENARIOS desc / builtin，1:1）。
 * label 统一引用 SCENARIO_LABELS 单一字典——防止列表页 / 编辑器 / 卡片文案再分叉。
 * 排序为**展示层手排**：系统内建场景（App 创建时预置）排前，不动 shared 枚举序。
 * **不含 `operational_popup`**：offer 的 scenario 是三值活动子集（A8 §6），运营弹窗是被动触发的 paywall
 * placement、不是活动页——列出它只会让运营选到一个必被 422 拒的值。
 */
export const OFFER_SCENARIO_ITEMS: {
  value: PaymentOfferScenario;
  label: string;
  desc: string;
  builtin?: boolean;
}[] = [
  {
    value: 'charge_center',
    label: SCENARIO_LABELS.charge_center,
    desc: '标准 COIN 充值（内建）',
    builtin: true,
  },
  {
    value: 'new_user_offer',
    label: SCENARIO_LABELS.new_user_offer,
    desc: '新人优惠页独立支付场景（内建）',
    builtin: true,
  },
  {
    value: 'limited_offer',
    label: SCENARIO_LABELS.limited_offer,
    desc: '营销节点高额 BONUS（Promo offer）',
  },
];

export const ELIGIBILITY_OPTIONS: { value: OfferEligibility; label: string }[] =
  OFFER_ELIGIBILITIES.map((v) => ({ value: v, label: ELIGIBILITY_LABELS[v] }));
